import * as React from 'react';
import styled from '@emotion/styled';

/**
 * DarkModeSwitch Component
 * Pill-shaped button with icon + text to toggle between light/dark themes
 * Design: Figma node 3:4362 (light) / 3:4364 (dark)
 */
const StyledSwitch = styled('button')`
  height: 40px;
  display: flex;
  align-items: center;
  gap: var(--gap-2x, 10px);
  padding: var(--gap-2x, 10px) var(--gap-3x, 15px);
  border: 1px solid var(--color-border-secondary);
  border-radius: var(--radius-round, 1000px);
  background: transparent;
  cursor: pointer;
  transition: all var(--transition-fast, 0.15s ease);
  white-space: nowrap;

  &:hover {
    background: var(--color-bg-box);
  }

  .theme-icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  .theme-label {
    font-family: var(--font-family-primary, 'Inter', sans-serif);
    font-size: var(--font-size-sm, 14px);
    font-weight: 500;
    line-height: 1.3;
    color: var(--color-info-quaternary);
  }

  @media (max-width: 992px) {
    border-color: transparent;
    padding: 0;

    &:hover {
      background: transparent;
    }

    .theme-label {
      display: none;
    }
  }
`;

// Sun emoji SVG for light mode
const SunIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clip-path="url(#clip0_83_1586)">
      <path
        d="M8.88889 1.11111C8.88889 1.11111 8.88889 0 10 0C11.1111 0 11.1111 1.11111 11.1111 1.11111V2.22222C11.1111 2.22222 11.1111 3.33333 10 3.33333C8.88889 3.33333 8.88889 2.22222 8.88889 2.22222V1.11111ZM18.8889 8.88889C18.8889 8.88889 20 8.88889 20 10C20 11.1111 18.8889 11.1111 18.8889 11.1111H17.7778C17.7778 11.1111 16.6667 11.1111 16.6667 10C16.6667 8.88889 17.7778 8.88889 17.7778 8.88889H18.8889ZM2.22222 8.88889C2.22222 8.88889 3.33333 8.88889 3.33333 10C3.33333 11.1111 2.22222 11.1111 2.22222 11.1111H1.11111C1.11111 11.1111 0 11.1111 0 10C0 8.88889 1.11111 8.88889 1.11111 8.88889H2.22222ZM5.06722 4.05167C5.06722 4.05167 5.85278 4.83722 5.06722 5.62278C4.28167 6.40833 3.49611 5.62278 3.49611 5.62278L2.71 4.83778C2.71 4.83778 1.92444 4.05222 2.71 3.26611C3.49611 2.48056 4.28167 3.26611 4.28167 3.26611L5.06722 4.05167ZM16.7339 15.7183C16.7339 15.7183 17.5194 16.5039 16.7339 17.2894C15.9483 18.075 15.1628 17.2894 15.1628 17.2894L14.3772 16.5039C14.3772 16.5039 13.5917 15.7183 14.3772 14.9328C15.1628 14.1472 15.9483 14.9328 15.9483 14.9328L16.7339 15.7183ZM16.5044 5.62278C16.5044 5.62278 15.7189 6.40833 14.9333 5.62278C14.1478 4.83722 14.9333 4.05167 14.9333 4.05167L15.7189 3.26611C15.7189 3.26611 16.5044 2.48056 17.29 3.26611C18.0756 4.05167 17.29 4.83722 17.29 4.83722L16.5044 5.62278ZM4.83778 17.2894C4.83778 17.2894 4.05222 18.075 3.26667 17.2894C2.48111 16.5039 3.26667 15.7183 3.26667 15.7183L4.05222 14.9328C4.05222 14.9328 4.83778 14.1472 5.62333 14.9328C6.40889 15.7183 5.62333 16.5039 5.62333 16.5039L4.83778 17.2894ZM8.88889 17.7778C8.88889 17.7778 8.88889 16.6667 10 16.6667C11.1111 16.6667 11.1111 17.7778 11.1111 17.7778V18.8889C11.1111 18.8889 11.1111 20 10 20C8.88889 20 8.88889 18.8889 8.88889 18.8889V17.7778Z"
        fill="#FFAC33"
      />
      <path
        d="M9.99989 15.5556C13.0681 15.5556 15.5554 13.0682 15.5554 10C15.5554 6.93175 13.0681 4.44444 9.99989 4.44444C6.93164 4.44444 4.44434 6.93175 4.44434 10C4.44434 13.0682 6.93164 15.5556 9.99989 15.5556Z"
        fill="#FFAC33"
      />
    </g>
    <defs>
      <clipPath id="clip0_83_1586">
        <rect width="20" height="20" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

// Moon emoji SVG for dark mode
const MoonIcon = () => (
  <svg className="theme-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M28.5 22.5C27.3 23.1 25.9 23.5 24.5 23.5C19.3 23.5 15 19.2 15 14C15 10.9 16.5 8.1 18.8 6.4C11.3 7.2 5.5 13.5 5.5 21.2C5.5 29.5 12.3 36 20.8 36C27.1 36 32.4 32.1 34.6 26.5C32.9 25.3 30.8 24.5 28.5 22.5Z"
      fill="#FFD983"
    />
    <path
      d="M28.5 22.5C30.8 24.5 32.9 25.3 34.6 26.5C35.5 24.2 36 21.7 36 19C36 8.5 27.5 0 17 0C16.2 0 15.4 0.1 14.6 0.2C17.9 2.3 20 6 20 10.2C20 16.9 14.6 22.3 8 22.3C6.3 22.3 4.7 22 3.2 21.4C5.3 29.5 12.5 35.5 21 35.5C25.6 35.5 29.7 33.7 32.7 30.8C31.1 28.3 29.5 25.5 28.5 22.5Z"
      fill="#5C913B"
      opacity="0.3"
    />
  </svg>
);

export const DarkModeSwitch = ({ isDarkThemeActive, toggleActiveTheme }) => (
  <StyledSwitch
    onClick={toggleActiveTheme}
    aria-label={isDarkThemeActive ? 'Switch to light mode' : 'Switch to dark mode'}
    type="button"
  >
    {isDarkThemeActive ? <MoonIcon /> : <SunIcon />}
    <span className="theme-label">{isDarkThemeActive ? 'Dark mode' : 'Light mode'}</span>
  </StyledSwitch>
);
